import React, { useState } from 'react';
import { trackCallConversion } from '../../utils/tracking';
import './DataRecovery.css';

// ─── SVG Icons ───────────────────────────────────────────────────────────────

const StarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#C98E32" stroke="none">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ClockIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const DatabaseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const UsbIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 2l20 20" />
    <path d="M8 8H4l2-3-2 3 2 3" />
    <path d="M16 8h4l-2-3 2 3-2 3" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

const ServerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const ZapIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const HardDriveIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="12" x2="2" y2="12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    <line x1="6" y1="16" x2="6.01" y2="16" />
    <line x1="10" y1="16" x2="10.01" y2="16" />
  </svg>
);

// ─── Data ────────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    id: 'hdd',
    icon: <HardDriveIcon />,
    title: 'Hard Drive Recovery',
    description: 'Recover data from failed, corrupted, or physically damaged HDDs. Our clean-room facility handles even the most severe mechanical failures.',
    image: '/images/hdd_recovery.jpg',
    features: ['Mechanical failure recovery', 'Logical damage repair', 'Bad sector recovery', 'Platter-level recovery'],
    successRate: '96%',
  },
  {
    id: 'ssd',
    icon: <ZapIcon />,
    title: 'SSD & NVMe Recovery',
    description: 'Specialized recovery for modern solid-state drives including NVMe M.2, SATA SSD, and flash storage with chip-level expertise.',
    image: '/images/ssd_recovery.jpg',
    features: ['NAND flash recovery', 'Controller failure fix', 'Firmware repair', 'Encrypted SSD support'],
    successRate: '92%',
  },
  {
    id: 'mobile',
    icon: <PhoneIcon />,
    title: 'Mobile Data Recovery',
    description: 'Retrieve lost contacts, photos, messages, and app data from Android and iOS devices — even with broken screens or water damage.',
    image: '/images/mobile_recovery.jpg',
    features: ['Android & iOS support', 'Broken screen recovery', 'Water-damaged devices', 'Deleted file retrieval'],
    successRate: '94%',
  },
  {
    id: 'usb',
    icon: <UsbIcon />,
    title: 'USB & Memory Card',
    description: 'Professional data recovery from USB flash drives, SD cards, CF cards, and all removable flash media formats.',
    image: '/images/usb_card_recovery.jpg',
    features: ['All SD/CF card formats', 'USB flash drives', 'Drone & camera cards', 'Formatted media recovery'],
    successRate: '95%',
  },
  {
    id: 'nas',
    icon: <ServerIcon />,
    title: 'NAS & RAID Recovery',
    description: 'Enterprise-grade recovery for NAS arrays, RAID 0/1/5/6/10 configurations, and server storage systems.',
    image: '/images/nas_raid_recovery.jpg',
    features: ['RAID 0 / 1 / 5 / 6 / 10', 'NAS array rebuilding', 'Server storage recovery', 'Synology & QNAP support'],
    successRate: '89%',
  },
  {
    id: 'deleted',
    icon: <DatabaseIcon />,
    title: 'Deleted File Recovery',
    description: 'Recover accidentally deleted photos, documents, videos, and files from any storage device — even after emptying the Recycle Bin.',
    image: '/images/data_process_lab.jpg',
    features: ['Photos & videos', 'Office documents', 'Formatted drives', 'After factory reset'],
    successRate: '97%',
  },
];

const PROCESS_STEPS = [
  {
    step: '01',
    title: 'Free Diagnosis',
    description: 'Bring your device or ship it to us. We perform a comprehensive diagnostic within 24 hours — completely free with no obligation.',
    icon: '🔍',
  },
  {
    step: '02',
    title: 'Recovery Quote',
    description: 'You receive a detailed recovery report and a transparent, no-surprise quote before we touch your data.',
    icon: '📋',
  },
  {
    step: '03',
    title: 'Expert Recovery',
    description: 'Our certified engineers use industry-leading tools in our clean-room facility to extract and reconstruct your data.',
    icon: '🛠️',
  },
  {
    step: '04',
    title: 'Secure Delivery',
    description: 'Recovered data is securely transferred to a new drive or storage device and handed back to you in person or shipped securely.',
    icon: '✅',
  },
];

const STATS = [
  { value: '98%', label: 'Overall Success Rate' },
  { value: '5000+', label: 'Cases Resolved' },
  { value: '24hrs', label: 'Average Turnaround' },
  { value: '100%', label: 'Confidential & Secure' },
];

const FAQ_DATA = [
  {
    q: 'How long does data recovery take?',
    a: 'Most standard recoveries are completed within 24–72 hours. Complex cases involving physical damage or RAID arrays may take 3–7 business days. We offer same-day emergency service for urgent situations.',
  },
  {
    q: 'Is my data kept confidential?',
    a: 'Absolutely. We operate under strict non-disclosure agreements and our facilities use enterprise-grade security. Your data is never shared, copied for any other purpose, or accessed beyond what is necessary for recovery.',
  },
  {
    q: 'What if my data cannot be recovered?',
    a: 'We operate on a No Data – No Charge policy. If we are unable to recover your data, you pay nothing for the recovery attempt (diagnostic fees may apply in some cases).',
  },
  {
    q: 'Do you handle physically damaged drives?',
    a: 'Yes. Our ISO-certified clean room handles severe physical failures including seized motors, head crashes, and platter damage that cannot be addressed in a standard lab environment.',
  },
  {
    q: 'Can you recover data from an encrypted drive?',
    a: 'In many cases yes — as long as you can provide the encryption password or key. We support BitLocker, FileVault, VeraCrypt, and most common encryption formats.',
  },
  {
    q: 'What storage devices do you support?',
    a: 'We support all major storage types: 3.5" & 2.5" HDDs, NVMe & SATA SSDs, SD cards, CF cards, USB drives, eMMC, mobile phone storage, NAS arrays, RAID systems, and more.',
  },
];

// ─── Sub-Components ──────────────────────────────────────────────────────────

function ServiceCard({ service }) {
  return (
    <div className="dr-service-card">
      <div className="dr-service-image-wrap">
        <img src={service.image} alt={service.title} className="dr-service-img" />
        <div className="dr-service-rate-badge">
          <StarIcon />
          <span>{service.successRate} success</span>
        </div>
      </div>
      <div className="dr-service-body">
        <div className="dr-service-icon-wrap">{service.icon}</div>
        <h3 className="dr-service-title">{service.title}</h3>
        <p className="dr-service-desc">{service.description}</p>
        <ul className="dr-service-features">
          {service.features.map((f, i) => (
            <li key={i} className="dr-feature-item">
              <CheckCircleIcon />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <a href="#contact-dr" className="dr-service-cta">
          <span>Get Free Quote</span>
          <ArrowRightIcon />
        </a>
      </div>
    </div>
  );
}

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className={`dr-faq-item ${isOpen ? 'dr-faq-item-open' : ''}`}>
      <button className="dr-faq-question" onClick={onToggle}>
        <span>{item.q}</span>
        <span className={`dr-faq-chevron ${isOpen ? 'dr-faq-chevron-open' : ''}`}>
          <ChevronDownIcon />
        </span>
      </button>
      <div className="dr-faq-answer-wrap" style={{ maxHeight: isOpen ? '300px' : '0px' }}>
        <p className="dr-faq-answer">{item.a}</p>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

function DataRecovery() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (i) => setOpenFaq(prev => (prev === i ? null : i));

  return (
    <main className="dr-page">

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="dr-hero" id="dr-hero">
        <div className="dr-hero-glow dr-hero-glow-left" aria-hidden="true" />
        <div className="dr-hero-glow dr-hero-glow-right" aria-hidden="true" />

        <div className="dr-hero-content">
          <div className="dr-hero-badge">
            <StarIcon />
            <span>DATA RECOVERY SPECIALISTS</span>
          </div>

          <h1 className="dr-hero-title">
            <span className="dr-title-white">We Recover</span>{' '}
            <span className="dr-title-gold">What Matters</span>
          </h1>

          <p className="dr-hero-subtitle">
            Industry-leading data recovery for hard drives, SSDs, mobile devices, RAID arrays, and
            removable media. <strong>No data — No charge.</strong> Trusted by 5,000+ customers.
          </p>

          <div className="dr-hero-actions">
            <a href="#dr-services" className="dr-btn-primary">
              Explore Services <ArrowRightIcon />
            </a>
            <a href="#contact-dr" className="dr-btn-secondary">
              Get Free Diagnosis
            </a>
          </div>

          <div className="dr-hero-trust-row">
            <span className="dr-trust-chip"><ShieldIcon /> No Data, No Charge</span>
            <span className="dr-trust-chip"><ClockIcon /> 24hr Turnaround</span>
            <span className="dr-trust-chip"><CheckCircleIcon /> 98% Success Rate</span>
          </div>
        </div>

        <div className="dr-hero-image-wrap">
          <div className="dr-hero-img-glow" aria-hidden="true" />
          <img
            src="/images/data_recovery_hero.jpg"
            alt="Professional data recovery laboratory"
            className="dr-hero-img"
          />
        </div>
      </section>

      {/* ── Stats Banner ─────────────────────────────────────────────── */}
      <section className="dr-stats-banner">
        {STATS.map((s, i) => (
          <div key={i} className="dr-stat-item">
            <span className="dr-stat-value">{s.value}</span>
            <span className="dr-stat-label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* ── Services Grid ────────────────────────────────────────────── */}
      <section className="dr-services-section" id="dr-services">
        <div className="dr-section-header">
          <div className="dr-section-badge">
            <StarIcon />
            <span>WHAT WE RECOVER</span>
          </div>
          <h2 className="dr-section-title">
            <span className="dr-title-white">Complete</span>{' '}
            <span className="dr-title-gold">Recovery Services</span>
          </h2>
          <div className="dr-divider" aria-hidden="true">
            <div className="dr-divider-line" />
            <div className="dr-divider-diamond" />
            <div className="dr-divider-line" />
          </div>
          <p className="dr-section-subtitle">
            From everyday accidents to catastrophic failures — we bring your data back.
          </p>
        </div>

        <div className="dr-services-grid">
          {SERVICES.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      </section>

      {/* ── Process Section ──────────────────────────────────────────── */}
      <section className="dr-process-section" id="dr-process">
        <div className="dr-section-header">
          <div className="dr-section-badge">
            <StarIcon />
            <span>HOW IT WORKS</span>
          </div>
          <h2 className="dr-section-title">
            <span className="dr-title-white">Our Recovery</span>{' '}
            <span className="dr-title-gold">Process</span>
          </h2>
          <div className="dr-divider" aria-hidden="true">
            <div className="dr-divider-line" />
            <div className="dr-divider-diamond" />
            <div className="dr-divider-line" />
          </div>
        </div>

        <div className="dr-process-steps">
          {PROCESS_STEPS.map((step, i) => (
            <React.Fragment key={step.step}>
              <div className="dr-step-card">
                <div className="dr-step-emoji">{step.icon}</div>
                <div className="dr-step-number">{step.step}</div>
                <h3 className="dr-step-title">{step.title}</h3>
                <p className="dr-step-desc">{step.description}</p>
              </div>
              {i < PROCESS_STEPS.length - 1 && (
                <div className="dr-step-connector" aria-hidden="true">
                  <ArrowRightIcon />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── Lab Showcase ─────────────────────────────────────────────── */}
      <section className="dr-lab-section">
        <div className="dr-lab-image-col">
          <img
            src="/images/data_process_lab.jpg"
            alt="Our ISO certified data recovery clean room"
            className="dr-lab-img"
          />
          <div className="dr-lab-overlay-badge">
            <ShieldIcon />
            <div>
              <p className="dr-lab-badge-title">ISO Certified Clean Room</p>
              <p className="dr-lab-badge-sub">Class 100 environment for physical recovery</p>
            </div>
          </div>
        </div>
        <div className="dr-lab-content-col">
          <div className="dr-section-badge" style={{ alignSelf: 'flex-start' }}>
            <StarIcon />
            <span>OUR FACILITY</span>
          </div>
          <h2 className="dr-lab-title">
            State-of-the-Art <span className="dr-title-gold">Clean Room Lab</span>
          </h2>
          <p className="dr-lab-desc">
            Physical data recovery requires a controlled environment to prevent microscopic
            contamination from damaging sensitive drive platters. Our Class 100 ISO-certified
            clean room maintains fewer than 100 particles per cubic foot — equivalent to a
            hospital operating theatre.
          </p>
          <ul className="dr-lab-points">
            {[
              'Certified Class 100 clean room environment',
              'Specialized head-swap & platter transplant tools',
              'Real-time monitoring for temperature & humidity',
              'Enterprise-grade imaging & recovery software',
              'Strict chain-of-custody for every device',
            ].map((point, i) => (
              <li key={i} className="dr-lab-point">
                <CheckCircleIcon />
                <span>{point}</span>
              </li>
            ))}
          </ul>
          <a href="#contact-dr" className="dr-btn-primary" style={{ alignSelf: 'flex-start', marginTop: '8px' }}>
            Book a Free Assessment <ArrowRightIcon />
          </a>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section className="dr-faq-section" id="dr-faq">
        <div className="dr-section-header">
          <div className="dr-section-badge">
            <StarIcon />
            <span>FREQUENTLY ASKED</span>
          </div>
          <h2 className="dr-section-title">
            <span className="dr-title-white">Common</span>{' '}
            <span className="dr-title-gold">Questions</span>
          </h2>
          <div className="dr-divider" aria-hidden="true">
            <div className="dr-divider-line" />
            <div className="dr-divider-diamond" />
            <div className="dr-divider-line" />
          </div>
        </div>

        <div className="dr-faq-list">
          {FAQ_DATA.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              isOpen={openFaq === i}
              onToggle={() => toggleFaq(i)}
            />
          ))}
        </div>
      </section>

      {/* ── CTA Section ──────────────────────────────────────────────── */}
      <section className="dr-cta-section" id="contact-dr">
        <div className="dr-cta-glow" aria-hidden="true" />
        <div className="dr-cta-content">
          <div className="dr-section-badge" style={{ justifyContent: 'center' }}>
            <StarIcon />
            <span>START RECOVERY TODAY</span>
          </div>
          <h2 className="dr-cta-title">
            Don't Lose Your <span className="dr-title-gold">Precious Data</span>
          </h2>
          <p className="dr-cta-subtitle">
            The sooner you act, the higher the chance of full recovery. Contact our experts now
            for a free, no-obligation diagnosis.
          </p>
          <div className="dr-cta-actions">
            <a
              href="tel:+918700319769"
              className="dr-btn-primary dr-btn-large"
              data-dsmr-phone="true"
              onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
            >
              📞 Call Now — Free Diagnosis
            </a>
            <a
              href="https://wa.me/918700319769"
              target="_blank"
              rel="noopener noreferrer"
              className="dr-btn-whatsapp"
            >
              💬 WhatsApp Us
            </a>
          </div>
          <p className="dr-cta-note">
            <ShieldIcon /> No Data, No Charge policy · Confidential & Secure · Available 7 days a week
          </p>
        </div>
      </section>

    </main>
  );
}

export default DataRecovery;
